import {useEffect, useId, useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {AlertCircle, ChevronLeft, Search, Users} from 'lucide-react';
import {RTC_PROTOCOL_V1, type RingTargetState} from '@phonehq/rtc-core';
import {useTeamMembers} from '../../../pages/Team/useTeamMembers';
import {fullName, type TeamMember} from '../../../pages/Team/teamFilters';
import {CallAvatar} from '../components/CallAvatar';
import {CallDialog, DialogButton} from '../components/CallDialog';
import {resolveMatrixUserIds} from '../../groupCalls/services/ringClient';
import {addPersonToCall, isRoomMember, matrixIdForUser, namedRoomOf, type AddPersonOutcome} from './addPeople';
import {groupSurfaceMessages as m} from './groupMessages';
import {presenceKey} from './teamPresence';

type RowState = 'add' | 'adding' | 'invited' | 'in_call';

const LOOKUP_BATCH = 100;

const FAILURES: Partial<Record<AddPersonOutcome, keyof typeof m>> = {
    full: 'ringFull',
    not_joined: 'ringNotJoined',
    failed: 'addFailed',
    unavailable: 'addFailed',
};


interface AddPeoplePanelProps {
    readonly roomId: string;
    readonly peopleInCall: ReadonlySet<string>;
    readonly invitees: readonly RingTargetState[];
    readonly onBack: () => void;
    readonly compact?: boolean;
}

export const AddPeoplePanel = ({roomId, peopleInCall, invitees, onBack, compact = false}: AddPeoplePanelProps) => {
    const intl = useIntl();
    const searchId = useId();
    const {members, isLoading} = useTeamMembers();
    const [query, setQuery] = useState('');
    const [matrixIds, setMatrixIds] = useState<ReadonlyMap<string, string>>(new Map());
    const [rowStates, setRowStates] = useState<ReadonlyMap<string, RowState>>(new Map());
    const [failures, setFailures] = useState<ReadonlyMap<string, keyof typeof m>>(new Map());
    const [confirm, setConfirm] = useState<{member: TeamMember; matrixUserId: string; room: string}>();
    const [error, setError] = useState(false);
    const [attempt, setAttempt] = useState(0);
    const max = RTC_PROTOCOL_V1.maxParticipants;

    useEffect(() => {
        const ids = members.map(member => Number(member.userId)).filter(Number.isFinite);
        if (ids.length === 0) {
            return;
        }
        let cancelled = false;
        const batches = Array.from({length: Math.ceil(ids.length / LOOKUP_BATCH)}, (_, index) => ids.slice(index * LOOKUP_BATCH, (index + 1) * LOOKUP_BATCH));
        Promise.all(batches.map(batch => resolveMatrixUserIds(batch)))
            .then(results => {
                if (cancelled) {
                    return;
                }
                setError(false);
                setMatrixIds(new Map(results.flatMap(result => [...result.entries()].map(([userId, matrixUserId]) => [String(userId), matrixUserId] as const))));
            })
            .catch(() => {
                if (!cancelled) {
                    setError(true);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [members, attempt]);
    const pending = invitees.filter(target => target.status === 'RINGING' || target.status === 'SILENT').length;
    const occupied = peopleInCall.size + pending;
    const full = occupied >= max;
    const invitedIds = useMemo(() => new Set(invitees.filter(target => target.status === 'RINGING' || target.status === 'SILENT').map(target => target.matrixUserId)), [invitees]);

    const visible = useMemo(() => {
        const needle = query.trim().toLowerCase();
        return members
            .filter(member => !needle || fullName(member).toLowerCase().includes(needle) || member.email.toLowerCase().includes(needle))
            .sort((left, right) => fullName(left).localeCompare(fullName(right)));
    }, [members, query]);

    const setRow = (key: string, state: RowState | undefined) => setRowStates(current => {
        const next = new Map(current);
        if (state) {
            next.set(key, state);
        } else {
            next.delete(key);
        }
        return next;
    });

    const ring = async (member: TeamMember, matrixUserId: string) => {
        const key = String(member.userId);
        setRow(key, 'adding');
        const outcome = await addPersonToCall(roomId, matrixUserId);
        const failure = FAILURES[outcome];
        setFailures(current => {
            const next = new Map(current);
            if (failure) {
                next.set(key, failure);
            } else {
                next.delete(key);
            }
            return next;
        });
        setRow(key, failure ? undefined : 'invited');
    };

    const add = async (member: TeamMember) => {
        const key = String(member.userId);
        setRow(key, 'adding');
        let matrixUserId = matrixIds.get(key);
        try {
            matrixUserId ??= await matrixIdForUser(Number(member.userId));
        } catch {
            matrixUserId = undefined;
            setError(true);
        }
        if (!matrixUserId) {
            setRow(key, undefined);
            setFailures(current => new Map(current).set(key, 'addFailed'));
            return;
        }
        setMatrixIds(current => new Map(current).set(key, matrixUserId as string));
        const room = namedRoomOf(roomId);
        if (room && !isRoomMember(roomId, matrixUserId)) {
            setRow(key, undefined);
            setConfirm({member, matrixUserId, room});
            return;
        }
        await ring(member, matrixUserId);
    };

    const stateOf = (member: TeamMember): RowState => {
        const key = String(member.userId);
        const matrixUserId = matrixIds.get(key);
        if (matrixUserId && peopleInCall.has(matrixUserId)) {
            return 'in_call';
        }
        if (matrixUserId && invitedIds.has(matrixUserId)) {
            return 'invited';
        }
        return rowStates.get(key) ?? 'add';
    };

    return (
        <div className={`flex min-h-0 flex-1 flex-col ${compact ? 'gap-2' : ''}`} data-testid="group-call-add-people">
            <header className={`flex items-center gap-2 ${compact ? '' : 'px-4 pb-2 pt-5'}`}>
                <button
                    type="button"
                    aria-label={intl.formatMessage(m.addBack)}
                    onClick={onBack}
                    className="phq-call-press inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[var(--phq-call-ink-muted)] hover:bg-[var(--phq-call-surface-muted)]"
                >
                    <ChevronLeft size={18} aria-hidden="true"/>
                </button>
                <h2 className="text-base font-semibold">
                    {full ? intl.formatMessage(m.addTitleCount, {count: occupied, max}) : intl.formatMessage(m.addPeople)}
                </h2>
            </header>
            <div className={compact ? '' : 'px-4'}>
                <label htmlFor={searchId} className="flex h-10 items-center gap-2 rounded-[10px] bg-[var(--phq-call-surface-muted)] px-3">
                    <Search size={16} className="shrink-0 text-[var(--phq-call-ink-muted)]" aria-hidden="true"/>
                    <input
                        id={searchId}
                        type="search"
                        value={query}
                        onChange={event => setQuery(event.target.value)}
                        placeholder={intl.formatMessage(m.addSearch)}
                        aria-label={intl.formatMessage(m.addSearch)}
                        className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-[var(--phq-call-ink-muted)]"
                    />
                </label>
            </div>
            {full && (
                <div role="status" className={`mt-2 rounded-xl bg-[var(--phq-call-warning-note)] px-3 py-2 text-[13px] text-[var(--phq-call-warning-ink)] ${compact ? '' : 'mx-4'}`}>
                    <p className="font-semibold">{intl.formatMessage(m.addFullTitle)}</p>
                    <p>{intl.formatMessage(m.addFullBody, {max})}</p>
                </div>
            )}
            {error && (
                <div role="alert" className="flex flex-col items-center gap-2 px-4 py-6 text-center">
                    <AlertCircle size={24} className="text-[var(--phq-call-ink-muted)]" aria-hidden="true"/>
                    <p className="text-sm font-semibold">{intl.formatMessage(m.addErrorTitle)}</p>
                    <p className="text-xs text-[var(--phq-call-ink-muted)]">{intl.formatMessage(m.addErrorBody)}</p>
                    <button type="button" onClick={() => setAttempt(current => current + 1)} className="phq-call-press mt-1 h-8 rounded-lg bg-[var(--phq-call-surface-muted)] px-3 text-xs font-semibold">
                        {intl.formatMessage(m.addRetry)}
                    </button>
                </div>
            )}
            {!error && !isLoading && visible.length === 0 && query.trim() && (
                <div className="flex flex-col items-center gap-2 px-4 py-6 text-center">
                    <Users size={24} className="text-[var(--phq-call-ink-muted)]" aria-hidden="true"/>
                    <p className="text-sm font-semibold">{intl.formatMessage(m.addEmptyTitle, {query: query.trim()})}</p>
                    <p className="text-xs text-[var(--phq-call-ink-muted)]">{intl.formatMessage(m.addEmptyBody)}</p>
                </div>
            )}
            {!error && isLoading && (
                <ul className="flex flex-col gap-2 px-4 py-3" aria-busy="true">
                    {[0, 1, 2].map(index => <li key={index} className="h-10 animate-pulse rounded-lg bg-[var(--phq-call-surface-muted)]"/>)}
                </ul>
            )}
            {!error && !isLoading && visible.length > 0 && (
                <>
                    <p className={`pb-1 pt-3 text-[11px] font-semibold uppercase tracking-[.04em] text-[var(--phq-call-ink-muted)] ${compact ? '' : 'px-4'}`}>
                        {intl.formatMessage(m.addSection)}
                    </p>
                    <ul className={`flex flex-col overflow-y-auto ${compact ? 'max-h-[280px]' : 'min-h-0 flex-1'}`}>
                        {visible.map(member => {
                            const name = fullName(member);
                            const key = String(member.userId);
                            const state = stateOf(member);
                            const failure = failures.get(key);
                            const subline = state === 'in_call'
                                ? m.addInThisCall
                                : state === 'invited'
                                    ? m.inviteRinging
                                    : failure ? m[failure] : m[presenceKey(member)];
                            return (
                                <li key={key} className={`flex h-12 items-center gap-3 ${compact ? '' : 'px-4'}`} data-testid="group-call-add-row">
                                    <CallAvatar identity={{name, avatarUrl: member.avatarUrl || undefined, userId: key}} size={32}/>
                                    <div className="min-w-0 flex-1">
                                        <p className="truncate text-sm font-medium">{name}</p>
                                        <p className="truncate text-xs text-[var(--phq-call-ink-muted)]">{intl.formatMessage(subline)}</p>
                                    </div>
                                    {state === 'add' || state === 'adding' ? (
                                        <button
                                            type="button"
                                            data-testid="group-call-add-person"
                                            disabled={state === 'adding' || full}
                                            aria-label={intl.formatMessage(m.addActionAria, {name})}
                                            onClick={() => void add(member)}
                                            className="phq-call-press inline-flex h-7 shrink-0 items-center rounded-lg bg-[var(--phq-call-brand)] px-3 text-xs font-semibold text-white disabled:opacity-40"
                                        >
                                            {intl.formatMessage(m.addAction)}
                                        </button>
                                    ) : (
                                        <span className="shrink-0 text-xs text-[var(--phq-call-ink-muted)]">
                                            {intl.formatMessage(state === 'in_call' ? m.peopleStatusInCall : m.peopleStatusInvited)}
                                        </span>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                </>
            )}
            {confirm && (
                <CallDialog
                    title={intl.formatMessage(m.addConfirmTitle, {name: fullName(confirm.member), room: confirm.room})}
                    width="sm"
                    onClose={() => setConfirm(undefined)}
                    footer={(
                        <>
                            <DialogButton tone="neutral" label={intl.formatMessage(m.addConfirmCancel)} onClick={() => setConfirm(undefined)}/>
                            <DialogButton
                                tone="brand"
                                testId="group-call-add-confirm"
                                label={intl.formatMessage(m.addConfirmAction)}
                                onClick={() => {
                                    const target = confirm;
                                    setConfirm(undefined);
                                    void ring(target.member, target.matrixUserId);
                                }}
                            />
                        </>
                    )}
                >
                    <p className="text-sm text-[var(--phq-call-ink-muted)]">
                        {intl.formatMessage(m.addConfirmBody, {name: fullName(confirm.member), room: confirm.room})}
                    </p>
                </CallDialog>
            )}
        </div>
    );
};
